// ─── El código de alumno, configurable por seminario (H6) ───────────────────
//
// QUÉ ES. Un identificador visible y corto del alumno —el que se dicta por
// teléfono, se imprime en el transcript y se busca en la ficha—. NO es el id
// interno ni el de la institución de origen.
//
// QUÉ SE CONFIGURA, por seminario (Configuración → Académico):
//   · prefijo   («L-», «SEM», o nada);
//   · estrategia: aleatorio (por defecto) o correlativo;
//   · dígitos;
//   · solo para el correlativo: si incluye el año (el correlativo se reinicia
//     cada año: L-2026-0001… → L-2027-0001…).
//
// SOLO AFECTA A LOS CÓDIGOS NUEVOS. Un código asignado no se reescribe nunca —ni
// al cambiar el formato—: está impreso en documentos y en la memoria de la gente.
// La unicidad la garantiza una reserva transaccional en el servidor.
/** Por defecto: aleatorio, 6 dígitos, sin prefijo. LABTS usa «L-», aleatorio,
 *  6 dígitos (se configura en el seminario, no acá). */
export const DEFAULT_STUDENT_CODE_FORMAT = {
    prefix: '',
    strategy: 'random',
    digits: 6,
};
export const STUDENT_CODE_LIMITS = { prefixMax: 6, digitsMin: 4, digitsMax: 9 };
const PREFIX_RE = /^[A-Za-z0-9-]*$/;
/** ¿El seminario ELIGIÓ un formato? SIN FORMATO GUARDADO NO SE ASIGNA NINGÚN
 *  CÓDIGO: la asignación empieza cuando el seminario guarda el suyo en
 *  Configuración → Académico. Nadie recibe un código con un formato que nadie
 *  eligió (decisión del PM, 2026-10-16). */
export function isStudentCodeConfigured(config) {
    return !!config?.studentCode && studentCodeFormatError(config.studentCode) === null;
}
/** El formato del seminario; sin configurar, el de por defecto (que solo se
 *  usa para MOSTRAR un punto de partida en la pantalla, no para asignar). */
export function resolveStudentCodeFormat(config) {
    const f = config?.studentCode;
    if (!f || studentCodeFormatError(f) !== null)
        return { ...DEFAULT_STUDENT_CODE_FORMAT };
    return {
        prefix: f.prefix,
        strategy: f.strategy,
        digits: f.digits,
        ...(f.strategy === 'sequential' && f.includeYear ? { includeYear: true } : {}),
    };
}
/** `null` si el formato es válido; si no, la CLAVE i18n del error. */
export function studentCodeFormatError(f) {
    if (typeof f.prefix !== 'string' || f.prefix.length > STUDENT_CODE_LIMITS.prefixMax) {
        return 'tenant.student_code.prefix_too_long';
    }
    if (!PREFIX_RE.test(f.prefix))
        return 'tenant.student_code.prefix_invalid';
    if (f.strategy !== 'random' && f.strategy !== 'sequential') {
        return 'tenant.student_code.strategy_invalid';
    }
    if (!Number.isInteger(f.digits) ||
        f.digits < STUDENT_CODE_LIMITS.digitsMin ||
        f.digits > STUDENT_CODE_LIMITS.digitsMax) {
        return 'tenant.student_code.digits_out_of_range';
    }
    if (f.includeYear && f.strategy !== 'sequential') {
        return 'tenant.student_code.year_only_sequential';
    }
    return null;
}
/** El código para el número `n` (y el año, si el formato lo lleva). */
export function formatStudentCode(f, n, year) {
    const num = String(n).padStart(f.digits, '0');
    const yearPart = f.strategy === 'sequential' && f.includeYear ? `${year ?? ''}-` : '';
    return `${f.prefix}${yearPart}${num}`;
}
/** Cuántos códigos distintos admite el formato (por año, si lleva año). */
export function studentCodeCapacity(f) {
    return 10 ** f.digits;
}
/** Un número al azar para un código aleatorio: 0 … 10^dígitos − 1. */
export function randomStudentCodeNumber(digits, rng = Math.random) {
    return Math.floor(rng() * 10 ** digits);
}
/** La clave del contador de un correlativo: uno solo, o uno por año. */
export function sequentialCounterKey(f, year) {
    return f.includeYear ? `sequential-${year}` : 'sequential';
}
/** Un ejemplo para mostrar en la configuración («L-048213», «SEM2026-0001»). */
export function studentCodeExample(f, year) {
    return f.strategy === 'sequential'
        ? formatStudentCode(f, 1, year)
        : formatStudentCode(f, Number('482130597'.slice(0, f.digits)), year);
}
//# sourceMappingURL=student-code.js.map